import type { SupabaseClient } from '@supabase/supabase-js';
import {
  SyncError,
  type OutboxOp,
  type PullResult,
  type PushResult,
  type Row,
  type SyncCursor,
  type SyncTable,
  type SyncTransport,
} from './types.ts';

interface PostgrestLikeError {
  message: string;
  code?: string;
}

/**
 * ¿Conviene reintentar? Red caída (status 0), timeouts, rate limit, sesión vencida o error
 * del servidor: sí. Permisos, validaciones o restricciones de la base: no (se rechaza).
 */
export function isRetryable(status: number, error: PostgrestLikeError): boolean {
  if (status === 0 || status === 401 || status === 408 || status === 429 || status >= 500) return true;
  return error.code === 'PGRST301' || error.code === 'PGRST303';
}

function toSyncError(status: number, error: PostgrestLikeError): SyncError {
  return new SyncError(error.message || `Error ${status}`, isRetryable(status, error));
}

interface ServerResult {
  op_id: string;
  status: 'ok' | 'rejected';
  error?: string;
  xid?: string;
  refetch?: boolean;
}

/** Ordena los resultados del servidor según las operaciones enviadas. */
export function matchResults(ops: OutboxOp[], results: ServerResult[]): PushResult[] {
  const byId = new Map(results.map((r) => [r.op_id, r]));
  return ops.map((op) => {
    const r = byId.get(op.op_id);
    if (!r) return { status: 'rejected', error: 'El servidor no informó el resultado de esta operación.' };
    return r.status === 'ok' ? { status: 'ok', xid: r.xid, ...(r.refetch ? { refetch: true } : {}) } : { status: 'rejected', error: r.error ?? 'Rechazado por el servidor.' };
  });
}

export class SupabaseTransport implements SyncTransport {
  constructor(private readonly client: SupabaseClient) {}

  async currentUserId(): Promise<string | null> {
    const { data } = await this.client.auth.getSession();
    return data.session?.user.id ?? null;
  }

  async pushBatch(ops: OutboxOp[]): Promise<PushResult[]> {
    const { data, error, status } = await this.client.rpc('sync_push', {
      p_ops: ops.map((op) => ({
        op_id: op.op_id,
        table: op.table,
        kind: op.kind,
        row_id: op.row_id,
        payload: op.payload,
        base: op.base,
        business_id: op.business_id,
        created_by: op.created_by,
        lic_jti: op.lic_jti,
        // Hora y dispositivo reales del cambio (puede haberse hecho sin conexión, horas antes).
        created_at: op.created_at,
        device_id: op.device_id,
      })),
    });
    // Cualquier falla del lote se reintenta: no se rechazan operaciones por un error general.
    if (error) throw new SyncError(error.message || `Error ${status}`, true);
    return matchResults(ops, (data ?? []) as ServerResult[]);
  }

  async pull(businessId: string, table: SyncTable, cursor: SyncCursor, limit: number): Promise<PullResult> {
    const { data, error, status } = await this.client.rpc('sync_pull', {
      p_business_id: businessId,
      p_table: table,
      p_after_xid: cursor.xid,
      p_after_id: cursor.id,
      p_limit: limit,
    });
    if (error) throw toSyncError(status, error);
    const result = data as { rows: Row[]; safe_xid: string };
    return { rows: result.rows, safeXid: result.safe_xid };
  }

  async fetchRows(table: SyncTable, ids: string[]): Promise<Row[]> {
    if (ids.length === 0) return [];
    const { data, error, status } = await this.client.from(table).select('*').in('id', ids);
    if (error) throw toSyncError(status, error);
    return (data ?? []) as Row[];
  }
}
